import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { getStats, listLeads, getProfile, STAGES } from "@/lib/api";
import { ArrowUpRight, Sparkles, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    getStats().then(setStats).catch(() => {});
    listLeads().then((l) => setRecent(l.slice(0, 10))).catch(() => {});
    getProfile().then(setProfile).catch(() => {});
  }, []);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString("en-GB", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }),
    []
  );

  const topLead = useMemo(() => {
    if (!recent.length) return null;
    return [...recent].sort((a, b) => (b.fit_score || 0) - (a.fit_score || 0))[0];
  }, [recent]);

  const tickerItems = useMemo(
    () =>
      Array.from({ length: 2 }).flatMap((_, k) =>
        STAGES.map((s) => ({
          key: `${k}-${s.id}`,
          count: String(stats?.by_stage?.[s.id] ?? 0).padStart(2, "0"),
          label: s.label,
        }))
      ),
    [stats]
  );

  const activePipeline =
    (stats?.by_stage?.contacted ?? 0) +
    (stats?.by_stage?.engaged ?? 0) +
    (stats?.by_stage?.negotiating ?? 0);

  return (
    <div className="min-h-screen">
      {/* MASTHEAD — magazine style */}
      <header className="border-b border-[#222]" data-testid="dashboard-masthead">
        <div className="px-12 pt-8 pb-4 flex items-center justify-between uppercase-mono text-[#8a8a8a]">
          <span>Issue 001 · {today}</span>
          <span className="flex items-center gap-3">
            <span className="inline-block w-2 h-2 bg-[#CCFF00] blink" />
            Live · {profile?.name || "Hub"}
          </span>
        </div>

        <div className="px-12 pb-10 pt-2 grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 lg:col-span-9">
            <h1
              className="font-serif font-light tracking-tight leading-[0.9] text-[88px] lg:text-[148px]"
              data-testid="dashboard-title"
            >
              Adi&apos;s Vantage <span className="text-[#CCFF00] italic">Hub</span>
              <br />
              at a glance.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-3 lg:text-right">
            <div className="uppercase-mono text-[#8a8a8a]">An editorial</div>
            <div className="uppercase-mono text-[#8a8a8a]">on outbound, for</div>
            <div className="uppercase-mono text-[#ededed]">creative technologists.</div>
            <Link
              to="/app/discover"
              data-testid="dashboard-cta-discover"
              className="btn-accent mt-6 px-6 py-3 inline-flex items-center gap-2 text-[12px] uppercase tracking-widest"
            >
              <Sparkles size={14} strokeWidth={1.5} /> Source new leads
            </Link>
          </div>
        </div>
      </header>

      {/* MARQUEE TICKER */}
      <div
        className="border-b border-[#222] overflow-hidden bg-[#CCFF00] text-black"
        data-testid="ticker"
      >
        <div className="flex whitespace-nowrap font-mono text-[12px] uppercase tracking-widest py-2.5 animate-[marquee_40s_linear_infinite]">
          {tickerItems.map((item) => (
            <span key={item.key} className="px-8 inline-flex items-center gap-2">
              <span className="opacity-60">[{item.count}]</span>
              <span>{item.label}</span>
              <span className="opacity-40">·</span>
            </span>
          ))}
        </div>
      </div>

      {/* SECTION 01 — PULSE: asymmetric stats */}
      <section className="border-b border-[#222] grid grid-cols-12" data-testid="stats-grid">
        {/* Big hero stat */}
        <div className="col-span-12 lg:col-span-6 p-12 border-r border-[#222] bg-[#CCFF00] text-black relative overflow-hidden">
          <div className="absolute top-6 left-12 right-12 flex items-center justify-between uppercase-mono">
            <span>01 — Pulse</span>
            <span>{`/ ${String(stats?.total_leads ?? 0).padStart(3, "0")}`}</span>
          </div>
          <div className="mt-16">
            <div className="uppercase-mono">Total prospects in motion</div>
            <div
              className="font-serif font-light leading-[0.85] text-[180px] lg:text-[240px] mt-2 tracking-tighter"
              data-testid="stat-total"
            >
              {stats?.total_leads ?? "—"}
            </div>
            <div className="font-mono text-sm mt-2">
              {activePipeline} actively engaged · avg fit {stats?.avg_fit ?? 0}/100
            </div>
          </div>
        </div>

        {/* Three secondary cells */}
        <div className="col-span-12 lg:col-span-6 grid grid-cols-1 md:grid-cols-2 grid-rows-2">
          <StatCell
            index="02"
            label="Avg fit score"
            value={stats?.avg_fit ?? "—"}
            suffix=" / 100"
            border="right-bottom"
          />
          <StatCell
            index="03"
            label="Engaged"
            value={stats?.by_stage?.engaged ?? 0}
            border="bottom"
            accent
          />
          <StatCell
            index="04"
            label="Activities logged"
            value={stats?.activities ?? 0}
            border="right"
          />
          <StatCell
            index="05"
            label="Won"
            value={stats?.by_stage?.won ?? 0}
            border="none"
            accent
          />
        </div>
      </section>

      {/* SECTION 02 — FEATURED PROSPECT (pull-quote treatment) */}
      {topLead && (
        <section className="border-b border-[#222] px-12 py-16" data-testid="featured-lead">
          <div className="uppercase-mono text-[#8a8a8a] mb-8 flex items-center justify-between">
            <span>02 — Featured prospect of the week</span>
            <span>fit · <span className="text-[#CCFF00]">{topLead.fit_score}</span></span>
          </div>
          <div className="grid grid-cols-12 gap-10 items-start">
            <div className="col-span-12 lg:col-span-2">
              <div className="uppercase-mono text-[#8a8a8a]">By</div>
              <div className="font-mono text-sm mt-1">{topLead.role || "—"}</div>
              <div className="uppercase-mono text-[#8a8a8a] mt-6">Co.</div>
              <div className="font-mono text-sm mt-1">{topLead.company || "—"}</div>
              <div className="uppercase-mono text-[#8a8a8a] mt-6">Stage</div>
              <div className="font-mono text-sm mt-1 text-[#CCFF00] uppercase">{topLead.stage}</div>
            </div>
            <div className="col-span-12 lg:col-span-10">
              <div className="font-serif text-[42px] lg:text-[68px] leading-[1.05] tracking-tight">
                <span className="text-[#CCFF00] mr-3">&ldquo;</span>
                {topLead.fit_reason || `${topLead.name} fits the studio's current trajectory.`}
                <span className="text-[#CCFF00] ml-3">&rdquo;</span>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-6 font-mono text-sm">
                <span className="text-[#ededed]">— {topLead.name}</span>
                <Link
                  to={`/app/leads/${topLead.id}`}
                  data-testid="featured-open-lead"
                  className="text-[#CCFF00] hover:underline inline-flex items-center gap-2 uppercase-mono"
                >
                  open file <ArrowRight size={12} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SECTION 03 — pipeline distribution + recent */}
      <section className="grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-5 p-12 border-r border-[#222]">
          <div className="uppercase-mono text-[#8a8a8a] mb-2">03 — Pipeline distribution</div>
          <h2 className="font-serif text-3xl mb-8">Where the work lives.</h2>
          <div className="space-y-5" data-testid="pipeline-distribution">
            {STAGES.map((s, i) => {
              const count = stats?.by_stage?.[s.id] ?? 0;
              const total = stats?.total_leads || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={s.id}>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="uppercase-mono text-[#ededed] flex items-baseline gap-3">
                      <span className="text-[#555]">{String(i + 1).padStart(2, "0")}</span>
                      {s.label}
                    </span>
                    <span className="font-mono text-[#8a8a8a]">
                      {count} · {pct}%
                    </span>
                  </div>
                  <div className="h-[3px] bg-[#1a1a1a] mt-2 relative overflow-hidden">
                    <div
                      className="h-full bg-[#CCFF00] transition-all duration-700 ease-out"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-7 p-12">
          <div className="flex items-end justify-between mb-2">
            <div className="uppercase-mono text-[#8a8a8a]">04 — Latest dispatches</div>
            <Link
              to="/app/pipeline"
              data-testid="dashboard-see-all"
              className="uppercase-mono text-[#CCFF00] hover:underline inline-flex items-center gap-2"
            >
              See all <ArrowRight size={12} strokeWidth={1.5} />
            </Link>
          </div>
          <h2 className="font-serif text-3xl mb-8">Today&apos;s desk.</h2>

          {recent.length === 0 ? (
            <div className="border border-dashed border-[#222] p-12 text-center" data-testid="empty-recent">
              <div className="font-serif text-3xl text-[#8a8a8a]">No prospects yet.</div>
              <Link
                to="/app/discover"
                className="uppercase-mono text-[#CCFF00] mt-3 inline-block hover:underline"
              >
                Run your first discovery →
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[#222] border-t border-[#222]">
              {recent.slice(0, 6).map((l, idx) => (
                <li key={l.id}>
                  <Link
                    to={`/app/leads/${l.id}`}
                    data-testid={`recent-lead-${l.id}`}
                    className="grid grid-cols-12 items-center gap-4 py-5 px-2 -mx-2 hover:bg-[#0F0F0F] transition-colors group"
                  >
                    <div className="col-span-1 uppercase-mono text-[#555] group-hover:text-[#CCFF00]">
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <div className="col-span-6 min-w-0">
                      <div className="font-serif text-2xl leading-tight truncate group-hover:text-[#CCFF00] transition-colors">
                        {l.name}
                      </div>
                      <div className="uppercase-mono text-[#8a8a8a] truncate mt-1">
                        {l.role || "—"} · {l.company || "—"}
                      </div>
                    </div>
                    <div className="col-span-2 font-mono text-[#CCFF00] text-lg">
                      {l.fit_score}
                    </div>
                    <div className="col-span-2 uppercase-mono text-[#8a8a8a]">{l.stage}</div>
                    <div className="col-span-1 flex justify-end">
                      <ArrowUpRight
                        size={18}
                        strokeWidth={1.5}
                        className="text-[#555] group-hover:text-[#CCFF00] transition-colors"
                      />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* COLOPHON */}
      <footer className="px-12 py-8 border-t border-[#222] flex items-center justify-between uppercase-mono text-[#555]">
        <span>Colophon · Cormorant Garamond + JetBrains Mono</span>
        <span>End of dispatch — go make work.</span>
      </footer>
    </div>
  );
}

function StatCell({ index, label, value, suffix = "", border = "none", accent = false }) {
  const borderClass =
    border === "right-bottom"
      ? "border-r border-b border-[#222]"
      : border === "right"
      ? "border-r border-[#222]"
      : border === "bottom"
      ? "border-b border-[#222]"
      : "";
  return (
    <div className={`p-8 ${borderClass} relative min-h-[180px] flex flex-col justify-between`}>
      <div className="flex items-center justify-between uppercase-mono text-[#8a8a8a]">
        <span>{label}</span>
        <span>{index}</span>
      </div>
      <div
        className={`font-serif text-[64px] lg:text-[88px] leading-none tracking-tighter ${
          accent ? "text-[#CCFF00]" : "text-[#ededed]"
        }`}
      >
        {value}
        {suffix && <span className="text-[#555] text-2xl">{suffix}</span>}
      </div>
    </div>
  );
}
